import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";
import { REGIONS } from "../constants.js";

const EMPTY_THING = { title: "", summary: "", link: "", region: "Highlands", image_url: "" };

export default function ThingsEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/things").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            if (data.id) {
                await api.put(`/admin/things/${data.id}`, data);
                toast.success("Updated.");
            } else {
                await api.post("/admin/things", data);
                toast.success("Added.");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        try {
            await api.delete(`/admin/things/${id}`);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (editing !== null) {
        return (
            <div className="card p-8 max-w-2xl" data-testid="thing-form">
                <h2 className="font-serif text-2xl mb-6">{editing.id ? "Edit" : "Add"} attraction</h2>
                <div className="space-y-5">
                    <Field label="Title"><input className="input-line" value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} data-testid="thing-form-title" /></Field>
                    <Field label="Region">
                        <select className="input-line bg-[var(--sb-surface)]" value={editing.region} onChange={(e) => setEditing({...editing, region: e.target.value})}>
                            {REGIONS.map((r) => <option key={r}>{r}</option>)}
                        </select>
                    </Field>
                    <Field label="Summary"><textarea className="input-line" rows={3} value={editing.summary} onChange={(e) => setEditing({...editing, summary: e.target.value})} /></Field>
                    <Field label="Link URL"><input className="input-line" value={editing.link} onChange={(e) => setEditing({...editing, link: e.target.value})} /></Field>
                    <Field label="Image URL (optional — falls back to region default)"><input className="input-line" value={editing.image_url || ""} onChange={(e) => setEditing({...editing, image_url: e.target.value})} placeholder="https://..." data-testid="thing-form-image-url" /></Field>
                </div>
                <div className="flex justify-end gap-2 mt-8">
                    <button onClick={() => setEditing(null)} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}}><X size={14} /> Cancel</button>
                    <button onClick={() => save(editing)} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="thing-form-save"><Check size={14} /> Save</button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="things-editor">
            <div className="flex justify-end mb-6">
                <button onClick={() => setEditing({ ...EMPTY_THING })} className="btn-primary" data-testid="admin-add-thing">
                    <Plus size={16} /> Add attraction
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((t) => (
                    <div key={t.id} className="card p-5 flex justify-between gap-4" data-testid={`admin-thing-${t.id}`}>
                        <div>
                            <div className="overline">{t.region}</div>
                            <div className="font-serif text-lg">{t.title}</div>
                            <p className="text-sm text-[var(--sb-text-dim)] mt-1">{t.summary}</p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            <button onClick={() => setEditing(t)} className="text-[var(--sb-text-dim)] hover:text-[var(--sb-brand)] p-1"><Edit3 size={16} /></button>
                            <button onClick={() => remove(t.id)} className="text-[var(--sb-text-dim)] hover:text-red-400 p-1"><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Submissions moderator                                                      */
